import React from "react";
import type { TestimonialsBlock, TestimonialItem } from "@/types/content";
import { StarIcon } from "@/components/icons";
import { fixedCmsImage } from "@/utils/cmsImage";
import styles from "./TestimonialsSection.module.css";

export interface TestimonialsSectionProps {
  block: TestimonialsBlock;
  className?: string;
}

interface StarRatingProps {
  rating: number;
  maxRating?: number;
}

/**
 * Star Rating - displays filled and empty stars for a rating value
 */
const StarRating: React.FC<StarRatingProps> = ({ rating, maxRating = 5 }) => {
  const clampedRating = Math.min(Math.max(Math.round(rating), 0), maxRating);

  return (
    <div
      className={styles.rating}
      role="img"
      aria-label={`${clampedRating} out of ${maxRating} stars`}
    >
      {Array.from({ length: maxRating }, (_, index) => (
        <StarIcon
          key={index}
          filled={index < clampedRating}
          className={
            index < clampedRating ? styles.starFilled : styles.starEmpty
          }
          aria-hidden="true"
        />
      ))}
    </div>
  );
};

interface TestimonialCardProps {
  testimonial: TestimonialItem;
}

/**
 * Testimonial Card - individual testimonial with rating, quote, and author
 */
const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial }) => {
  const { rating, quote, authorName, authorTitle, authorAvatar } = testimonial;

  return (
    <article className={styles.card}>
      <StarRating rating={rating} />

      <blockquote className={styles.quote}>
        <p>{quote}</p>
      </blockquote>

      <footer className={styles.author}>
        {authorAvatar?.url && (
          <img
            {...fixedCmsImage(authorAvatar.url, { width: 48 })}
            alt=""
            className={styles.avatar}
            loading="lazy"
          />
        )}
        <div className={styles.authorInfo}>
          <span className={styles.authorName}>{authorName}</span>
          {authorTitle && (
            <span className={styles.authorTitle}>{authorTitle}</span>
          )}
        </div>
      </footer>
    </article>
  );
};

/**
 * Testimonials Section - displays customer testimonials in a responsive grid
 */
export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  block,
  className,
}) => {
  const {
    heading,
    backgroundColor = "white",
    testimonials,
    columns = "3",
  } = block;

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  const containerClasses = [styles.section, className]
    .filter(Boolean)
    .join(" ");

  const gridClasses = [styles.grid, styles[`cols${columns}`]]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={containerClasses}
      data-component="testimonials-section"
      data-bg={backgroundColor}
    >
      <div className={styles.container}>
        {heading && <h2 className={styles.heading}>{heading}</h2>}

        <div
          className={gridClasses}
          role="list"
          aria-label="Customer testimonials"
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id || index}
              className={styles.gridItem}
              role="listitem"
            >
              <TestimonialCard testimonial={testimonial} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
